<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <content-dialog
    :visible.sync="modalVisible"
    max-width="700px"
    :scrollable="true"
  >
    <template slot="dialogTitle">
      <span>
        Set yacht investment agreement details
      </span>
      <div class="body-1">
        <v-row
          no-gutters
          align="center"
          class="list--bordered py-2"
        >
          <v-col>
            {{ financeFirstPayment ? 'First payment' : 'Total payment' }}
          </v-col>
          <v-col
            cols="auto"
            class="font-weight-bold"
          >
            {{ financeFirstPayment || financeTotalPayment | currency(yacht.currency) }}
          </v-col>
        </v-row>
        <v-row
          v-if="financeLastPayment"
          no-gutters
          align="center"
          class="list--bordered py-2"
        >
          <v-col>
            Last payment
          </v-col>
          <v-col
            cols="auto"
            class="font-weight-bold"
          >
            {{ financeLastPayment | currency(yacht.currency) }}
          </v-col>
        </v-row>
      </div>
    </template>

    <v-card-text class="pa-6">
      <!--<editor-fold description="First payment plan">-->
      <v-row
        no-gutters
        align="center"
        class="mb-2"
      >
        <v-col>
          <v-subheader v-if="financeFirstPayment">
            First payment plan
          </v-subheader>
          <v-subheader v-else>
            Payment plan
          </v-subheader>
        </v-col>
        <v-col cols="auto">
          <v-btn
            color="primary"
            class="ma-0"
            small
            @click="addPayment()"
          >
            <v-icon
              left
              small
            >
              fa fa-plus
            </v-icon>
            Add Payment
          </v-btn>
        </v-col>
      </v-row>

      <validation-observer ref="validationObserver">
        <v-row
          v-for="(payment, i) in payments"
          :key="'payment_'+i"
          dense
        >
          <v-col cols="7">
            <validation-provider
              v-slot="{ errors }"
              rules="required"
              name="Amount"
            >
              <currency-input
                v-model="payment.amount"
                :suffix="yacht.currency"
                :precision="0"
                outlined
                label="Amount"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
          <v-col cols="4">
            <v-menu
              v-model="payment.datePicker"
              :close-on-content-click="false"
              transition="fade-transition"
              min-width="290px"
              absolute
              nudge-bottom="60px"
            >
              <template v-slot:activator="{ on }">
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                  name="Due date"
                >
                  <v-text-field
                    :value="payment.dueDate ? $moment(payment.dueDate).format('DD.MM.YYYY.') : ''"
                    outlined
                    label="Due date"
                    readonly
                    :error-messages="errors"
                    v-on="on"
                  />
                </validation-provider>
              </template>
              <v-date-picker
                v-model="payment.dueDate"
                no-title
                :first-day-of-week="1"
                @input="payment.datePicker = false"
              />
            </v-menu>
          </v-col>
          <v-col class="pl-2">
            <v-checkbox
              v-model="payment.isDeposit"
              label="Deposit"
              hide-details
              class="mt-2"
            />
          </v-col>
          <v-col cols="auto">
            <v-btn
              small
              icon
              color="red"
              class="mt-3"
              @click="removePayment(i)"
            >
              <v-icon
                small
              >
                fa fa-times
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-row
          dense
        >
          <v-col cols="7">
            <currency-input
              :value="remainingPaymentAmount"
              :suffix="yacht.currency"
              :precision="0"
              outlined
              readonly
              label="Remaining payment"
            />
          </v-col>
          <v-col cols="4">
            <v-menu
              v-model="remainingPayment.datePicker"
              :close-on-content-click="false"
              transition="fade-transition"
              min-width="290px"
              absolute
              nudge-bottom="60px"
            >
              <template v-slot:activator="{ on }">
                <validation-provider
                  v-slot="{ errors }"
                  name="Due date"
                >
                  <v-text-field
                    :value="remainingPayment.dueDate ? $moment(remainingPayment.dueDate).format('DD.MM.YYYY.') : ''"
                    outlined
                    label="Due date"
                    readonly
                    :error-messages="errors"
                    v-on="on"
                  />
                </validation-provider>
              </template>
              <v-date-picker
                v-model="remainingPayment.dueDate"
                no-title
                :first-day-of-week="1"
                @input="remainingPayment.datePicker = false"
              />
            </v-menu>
          </v-col>
        </v-row>

        <div v-if="$can('edit-insured-value', 'Yacht')">
          <v-subheader>
            Insured Value
          </v-subheader>

          <v-row
            dense
          >
            <v-col cols="7">
              <currency-input
                v-model="insuredValue"
                :suffix="yacht.currency"
                :precision="0"
                outlined
                label="Insured value"
              />
            </v-col>
          </v-row>
        </div>

        <v-subheader>
          Financing
        </v-subheader>

        <v-row
          dense
          align="center"
          class="mb-4"
        >
          <v-col cols="6">
            <v-checkbox
              v-model="subjectToFinancing"
              label="Subject to financing"
            />
          </v-col>
          <v-col cols="5">
            <v-menu
              v-model="financingDecisionDatePicker"
              :close-on-content-click="false"
              transition="fade-transition"
              min-width="290px"
              absolute
              nudge-bottom="60px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="financingDecisionDate ? $moment(financingDecisionDate).format('DD.MM.YYYY.') : ''"
                  outlined
                  label="Accept/reject date"
                  readonly
                  hide-details
                  clearable
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="financingDecisionDate"
                no-title
                :first-day-of-week="1"
                @input="financingDecisionDatePicker = false"
              />
            </v-menu>
          </v-col>
        </v-row>

        <v-subheader>
          Other agreed sales terms
        </v-subheader>

        <tiny-mce
          id="agreement_details_editor"
          key="agreement_details_editor"
          v-model="specialDeals"
          width="auto"
          simple
        />

        <v-subheader class="mt-4">
          Other agreed operational terms
        </v-subheader>

        <tiny-mce
          id="agreement_details_editor_operational"
          key="agreement_details_editor_operational"
          v-model="operationalTerms"
          width="auto"
          simple
        />
      </validation-observer>
    </v-card-text>

    <template slot="dialogActions">
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="save"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
  </content-dialog>
</template>

<script>
import ContentDialog from '@/components/helpers/Dialog'
import CurrencyInput from '@/components/helpers/CurrencyInput'
import cloneDeep from 'lodash/cloneDeep'
import TinyMce from '@/components/helpers/TinyMce'
import Yacht from '@/models/Yacht'

export default {
    components: {
      TinyMce,
      CurrencyInput,
      ContentDialog
    },
    props: {
      visible: Boolean,
      yacht: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        agreementDetailsDirty: cloneDeep(Yacht.emptyModel.agreement_details),
        specialDeals: '',
        operationalTerms: '',
        submitting: false,
        payments: [],
        remainingPayment: {
          dueDate: null,
          datePicker: false
        },
        insuredValue: null,
        subjectToFinancing: false,
        financingDecisionDate: null,
        financingDecisionDatePicker: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      financeFirstPayment() {
        if (!this.yacht.finance_model_calculation) return false

        return this.yacht.finance_model_calculation.investor.first_payment || false
      },
      financeLastPayment() {
        if (!this.yacht.finance_model_calculation) return false

        return this.yacht.finance_model_calculation.investor.last_payment || false
      },
      financeTotalPayment() {
        if (!this.yacht.finance_model_calculation || this.financeFirstPayment) return false

        return this.yacht.finance_model_calculation.investor.sale_price
      },
      financePayment() {
        return this.financeFirstPayment || this.financeTotalPayment
      },
      paymentAmount() {
        if (this.payments.length === 0) return 0

        return this.payments.map(payment => Number(payment.amount)).reduce((a, b) => a + b)
      },
      remainingPaymentAmount() {
        return Number(this.financePayment) - Number(this.paymentAmount)
      }
    },
    watch: {
      yacht: function (yacht) {
        this.loadCurrentAgreementDetails()
      }
    },
    created() {
      this.loadCurrentAgreementDetails()
    },
    methods: {
      loadCurrentAgreementDetails() {
        if (this.yacht.agreement_details) {
          this.specialDeals = this.yacht.agreement_details.special_deals
          this.operationalTerms = this.yacht.agreement_details.operational_terms

          if (this.yacht.agreement_details.insured_value) {
            this.insuredValue = this.yacht.agreement_details.insured_value
          } else {
            this.insuredValue = Number(this.yacht.total_sale_price_final)
          }

          this.subjectToFinancing = this.yacht.agreement_details.subject_to_financing || false
          this.financingDecisionDate = this.yacht.agreement_details.financing_decision_date || null
        }

        if (this.yacht.ex_factory_date) {
          this.remainingPayment.dueDate = this.$moment(this.yacht.ex_factory_date).subtract(30, 'days').format('YYYY-MM-DD')
        }

        if (!this.yacht.agreement_details) return
        if (!this.yacht.agreement_details.first_payment) return
        if (!this.yacht.agreement_details.first_payment.payments) return
        if (this.yacht.agreement_details.first_payment.payments.length === 0) return

        const payments = cloneDeep(this.yacht.agreement_details.first_payment.payments)

        const remainingPayment = payments.pop()

        this.remainingPayment.dueDate = remainingPayment.due_date

        this.payments = payments.map(p => {
          return {
            amount: p.amount,
            dueDate: p.due_date,
            isDeposit: p.is_deposit,
            datePicker: false
          }
        })
      },
      async save() {
        let validation = await this.$refs.validationObserver.validate()

        if (!validation) return

        try {
          this.submitting = true

          let data = {
            special_deals: this.specialDeals,
            operational_terms: this.operationalTerms,
            insured_value: this.insuredValue,
            subject_to_financing: this.subjectToFinancing,
            financing_decision_date: this.financingDecisionDate,
            first_payment: {}
          }

          if (this.payments.length > 0) {
            data.first_payment.payments = this.payments.map(p => {
              return {
                amount: p.amount,
                due_date: p.dueDate,
                is_deposit: p.isDeposit
              }
            })
          } else {
            data.first_payment.payments = []
          }

          if (this.remainingPayment.dueDate) {
            data.first_payment.payments.push({
              amount: this.remainingPaymentAmount,
              due_date: this.remainingPayment.dueDate,
              is_deposit: false
            })
          }

          let result = await Yacht.api.storeAgreementDetails(this.yacht.id, data)

          this.$store.dispatch('alert', {
            text: 'Agreement details saved.',
            color: 'success'
          })

          this.$emit('update:yacht', result.yacht)

          this.modalVisible = false
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong!',
            color: 'error'
          })
          throw e
        } finally {
          this.submitting = false
        }
      },
      addPayment() {
        this.payments.push({
          amount: null,
          dueDate: null,
          isDeposit: true,
          datePicker: false
        })
      },
      removePayment(i) {
        this.payments.splice(i, 1)
      }
    }
  }
</script>
